import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Calculator, Sparkle, CurrencyDollar, ChartBar } from "@phosphor-icons/react";

const C = { teal: "#0A8B85", tealSoft: "#7BD3CE", orange: "#F59E0B", green: "#9AE6B4", amber: "#FCD34D", red: "#F87171" };

export default function ValuationsPanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/valuations/stats")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load valuations stats"));
  }, []);

  if (!data) return <div className="text-white/45 text-sm">Loading…</div>;

  const maxCount = Math.max(1, ...data.sectors.map((s) => s.count));

  return (
    <section data-testid="admin-valuations-section">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <Tile label="Total started" value={data.total_started} color={C.tealSoft} icon={Calculator} testid="vt-total" />
        <Tile label="Finalised" value={data.finalised} color={C.amber} icon={Sparkle} testid="vt-finalised" />
        <Tile label="Advisor reviews paid" value={data.advisor_reviews_paid} color={C.green} icon={ChartBar} testid="vt-reviews" />
        <Tile label="Review revenue (USD)" value={`$${(data.advisor_review_revenue_usd || 0).toLocaleString()}`} color={C.orange} icon={CurrencyDollar} testid="vt-revenue" />
      </div>

      <div className="rounded-2xl p-6 border" style={{ borderColor: "rgba(123, 211, 206, 0.12)" }}>
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold mb-4" style={{ color: C.tealSoft }}>
          Finalised valuations by sector
        </div>
        {data.sectors.length === 0 ? (
          <div className="text-sm text-white/40">No finalised valuations yet.</div>
        ) : (
          <div className="space-y-2.5" data-testid="vt-sector-breakdown">
            {data.sectors.map((s) => (
              <div key={s.sector} className="flex items-center gap-3">
                <span className="text-[12px] uppercase tracking-wider w-32 truncate" style={{ color: "rgba(255,255,255,0.7)" }}>
                  {s.sector}
                </span>
                <div className="flex-1 h-5 rounded-sm overflow-hidden"
                  style={{ backgroundColor: "rgba(255,255,255,0.05)" }}>
                  <div className="h-full transition-all"
                    style={{ width: `${(s.count / maxCount) * 100}%`, backgroundColor: C.teal }}></div>
                </div>
                <span className="text-[12px] font-mono-num font-bold w-10 text-right" style={{ color: "#fff" }}>{s.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Paid advisor reviews — SLA queue */}
      <div className="mt-6 rounded-2xl p-6 border" style={{ borderColor: "rgba(245, 158, 11, 0.2)" }}>
        <div className="flex items-baseline justify-between mb-4">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: C.orange }}>
            Paid advisor reviews — SLA queue
          </div>
          <div className="text-[10px] font-mono-num" style={{ color: "rgba(255,255,255,0.4)" }}>
            Target: ≤ 24 h to first contact
          </div>
        </div>
        {(data.review_queue || []).length === 0 ? (
          <div className="text-sm text-white/40">No paid reviews waiting. When a founder pays for a review it appears here.</div>
        ) : (
          <table className="w-full text-[12px]" data-testid="vt-review-queue">
            <thead>
              <tr style={{ color: "rgba(255,255,255,0.5)" }}>
                <th className="text-left py-2 font-semibold uppercase tracking-wider">Founder</th>
                <th className="text-left py-2 font-semibold uppercase tracking-wider">Company</th>
                <th className="text-left py-2 font-semibold uppercase tracking-wider">Sector</th>
                <th className="text-left py-2 font-semibold uppercase tracking-wider">Waiting</th>
                <th className="text-left py-2 font-semibold uppercase tracking-wider">Status</th>
                <th className="text-right py-2 font-semibold uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.review_queue.map((r) => (
                <ReviewRow key={r.valuation_id} row={r} onChanged={() => location.reload()} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function ReviewRow({ row, onChanged }) {
  const [busy, setBusy] = useState(false);
  const elapsed = useElapsedSince(row.advisor_review_paid_at);
  const stale = elapsed.totalHours >= 24;
  const status = row.advisor_review_status || "requested";

  const mark = async (next) => {
    setBusy(true);
    try {
      await api.post(`/admin/valuations/${row.valuation_id}/review-status`, { status: next });
      toast.success(`Marked ${next.replace("_", " ")}`);
      onChanged();
    } catch (e) {
      toast.error("Could not update status");
    } finally { setBusy(false); }
  };

  return (
    <tr style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }} data-testid={`vt-review-row-${row.valuation_id}`}>
      <td className="py-3" style={{ color: "rgba(255,255,255,0.85)" }}>
        <div className="font-bold">{row.user_name || "—"}</div>
        <div className="text-[11px]" style={{ color: "rgba(255,255,255,0.4)" }}>{row.user_email}</div>
      </td>
      <td className="py-3 font-bold text-white">{row.company_name}</td>
      <td className="py-3" style={{ color: "rgba(255,255,255,0.7)" }}>{row.sector}</td>
      <td className="py-3 font-mono-num font-bold"
        style={{ color: stale ? C.red : (elapsed.totalHours >= 12 ? C.amber : C.green) }}>
        {elapsed.label}
      </td>
      <td className="py-3">
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full"
          style={{
            backgroundColor: status === "completed" ? `${C.green}22` : (status === "in_progress" ? `${C.amber}22` : `${C.orange}22`),
            color: status === "completed" ? C.green : (status === "in_progress" ? C.amber : C.orange),
          }}>{status.replace("_", " ")}</span>
      </td>
      <td className="py-3 text-right">
        <div className="inline-flex gap-1">
          {status === "requested" && (
            <button disabled={busy} onClick={() => mark("in_progress")}
              data-testid={`vt-mark-progress-${row.valuation_id}`}
              className="text-[10px] font-bold uppercase tracking-[0.12em] px-2.5 py-1 rounded transition-colors disabled:opacity-50"
              style={{ border: `1px solid ${C.amber}55`, color: C.amber }}>
              Start
            </button>
          )}
          {status !== "completed" && (
            <button disabled={busy} onClick={() => mark("completed")}
              data-testid={`vt-mark-complete-${row.valuation_id}`}
              className="text-[10px] font-bold uppercase tracking-[0.12em] px-2.5 py-1 rounded transition-colors disabled:opacity-50"
              style={{ border: `1px solid ${C.green}55`, color: C.green }}>
              Complete
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function useElapsedSince(iso) {
  if (!iso) return { label: "—", totalHours: 0 };
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMin = Math.floor((now - then) / 60_000);
  if (diffMin < 60) return { label: `${diffMin}m`, totalHours: diffMin / 60 };
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 48) return { label: `${diffH}h`, totalHours: diffH };
  const diffD = Math.floor(diffH / 24);
  return { label: `${diffD}d`, totalHours: diffH };
}

function Tile({ label, value, color, icon: Icon, testid }) {
  return (
    <div className="rounded-xl p-4 border" data-testid={testid}
      style={{ borderColor: "rgba(255,255,255,0.06)", backgroundColor: "rgba(0,0,0,0.3)" }}>
      <div className="flex items-center gap-1.5">
        <Icon size={11} weight="fill" style={{ color }} />
        <span className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: "rgba(255,255,255,0.55)" }}>
          {label}
        </span>
      </div>
      <div className="font-display font-black mt-2" style={{ fontSize: "1.85rem", color, letterSpacing: "-0.02em" }}>
        {value}
      </div>
    </div>
  );
}
